/**
 * A tiny, dependency-free mirror of the signed-in session that shared
 * components can read WITHOUT importing the auth context (src/lib/auth.tsx),
 * which in turn imports the API client and React context machinery.
 *
 * `AuthProvider` is the only writer. Readers use `useSessionInfo()` so they
 * re-render when the provider resolves `/api/auth/me` after first paint.
 */
import { useSyncExternalStore } from 'react'

export type SessionInfo = {
  /** True when the signed-in user is an admin, or when auth is off. */
  isAdmin: boolean
  /** Sign-in state as the provider knows it. */
  mode: 'loading' | 'open' | 'authed' | 'login'
}

let current: SessionInfo = { isAdmin: false, mode: 'loading' }
const listeners = new Set<() => void>()

export function setSessionInfo(next: SessionInfo): void {
  if (next.isAdmin === current.isAdmin && next.mode === current.mode) return
  current = next
  for (const l of listeners) l()
}

export function getSessionInfo(): SessionInfo {
  return current
}

function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => { listeners.delete(l) }
}

export function useSessionInfo(): SessionInfo {
  return useSyncExternalStore(subscribe, getSessionInfo, getSessionInfo)
}
